import { RefObject } from 'react';

import styled from 'styled-components';

import { DESCRIPTION_LINE_COUNT_LIMIT } from '@components/action-card';
import { getComparatorLabel, getValueLabel } from '@components/integrations';
import { getScopedTranslate, styledTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { Font } from '@styles/theme';

import { ConditionComparator, JourneyActionValuesTypes } from 'api/types';

type ActionType = 'condition_v2';

type GetConditionDescriptionProps<Styled extends boolean = true> = {
  values: JourneyActionValuesTypes[ActionType];
  styled?: Styled;
  locale?: typeof descriptionLocale;
  isInNode?: boolean;
  isTextClamped?: boolean;
  textClampedRef?: RefObject<HTMLDivElement>;
};

type ConditionDescriptionElement = JSX.Element & {
  ref?: RefObject<any>;
};

export function getConditionDescription(props: GetConditionDescriptionProps<false>): string;
export function getConditionDescription(
  props: GetConditionDescriptionProps<true>
): ConditionDescriptionElement;
export function getConditionDescription({
  values,
  styled = true,
  locale = descriptionLocale,
  isInNode = true,
  textClampedRef,
}: GetConditionDescriptionProps<boolean>): ConditionDescriptionElement | string {
  const { t } = getScopedTranslate(locale);

  const conditions = values?.conditions || [];
  if (conditions.length === 0) {
    return t('configureAction');
  }

  const renderConditionsCount =
    conditions.length > DESCRIPTION_LINE_COUNT_LIMIT
      ? DESCRIPTION_LINE_COUNT_LIMIT - 1
      : DESCRIPTION_LINE_COUNT_LIMIT;

  const comparatorElements = conditions.map((condition, index) => {
    if (styled && index >= renderConditionsCount) {
      return null;
    }
    const { field, comparator, value } = condition;
    const valueLabel = getValueLabel(value, comparator);
    const fieldLabel = field?.variables && Object.values(field.variables)[0].label;

    if (!fieldLabel || !comparator || (valueLabel === '' && !comparator.includes('empty'))) {
      return '';
    }

    const comparatorString = getComparatorLabel(comparator as ConditionComparator) || comparator;

    if (styled) {
      const conjunction = values?.conditionType === 'all' ? t('core.and') : t('core.or');
      const commonProps = { font: 'regular14' as Font, style: { display: 'inline' } };
      return (
        <ConditionContainer key={index}>
          {styledTranslate({
            key: 'conditionDescription',
            variables: {
              conjunction: {
                text: index > 0 ? `${conjunction} ` : '',
                color: 'textSecondary',
                capitalize: true,
                ...commonProps,
              },
              field: {
                text: fieldLabel,
                color: 'elementBlue100',
                ...commonProps,
              },
              comparator: {
                text: comparatorString,
                color: 'textSecondary',
                ...commonProps,
              },
              value: {
                text: valueLabel,
                color: 'textSecondary',
                ...commonProps,
              },
            },
            translations: descriptionLocale,
          })}
        </ConditionContainer>
      );
    } else {
      return t('conditionDescriptionNoStyle', {
        field: fieldLabel,
        comparator: comparatorString,
        value: valueLabel,
      });
    }
  });
  if (styled) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }} ref={textClampedRef}>
        {isInNode && t('continueIf')}
        {comparatorElements}
      </div>
    );
  } else {
    const conditionStrings = comparatorElements as string[];
    const joinedConditionString = conditionStrings.join(
      values?.conditionType === 'all' ? `\n${t('and')} ` : `\n${t('or')} `
    );

    return t('continueIfNoStyle', { conditions: joinedConditionString });
  }
}

type GetConditionTooltipProps = Omit<GetConditionDescriptionProps, 'styled' | 'inNode'>;

export const getConditionTooltip = (props: GetConditionTooltipProps) => {
  const { values, isTextClamped } = props;
  const conditions = values?.conditions ?? [];
  const isAllConditionsVisible =
    conditions.length <= DESCRIPTION_LINE_COUNT_LIMIT && !isTextClamped;
  return isAllConditionsVisible ? '' : getConditionDescription({ values, styled: false });
};

export const descriptionLocale = {
  en: {
    configureAction: 'Configure action',
    continueIf: 'Continue if\n',
    continueIfNoStyle: 'Continue if\n{conditions}',
    conditionDescription: '{conjunction}{field} {comparator} {value}',
    conditionDescriptionNoStyle: '{field} {comparator} {value}',
    is: 'is',
    isNot: 'is not',
    booleanTrue: 'true',
    booleanFalse: 'false',
    before: 'Before',
    isOrBefore: 'Is or before',
    isOrAfter: 'Is or after',
    after: 'After',
    and: 'AND',
    or: 'OR',
    contains: 'contains',
    notContain: 'does not contain',
    equalTo: 'equal to',
    notEqualTo: 'not equal to',
    empty: 'is empty',
    notEmpty: 'is not empty',
    anyOf: 'is any of',
    noneOf: 'is none of',
    moreThan: 'more than',
    moreOrEqualTo: 'more or equal to',
    lessThan: 'less than',
    lessOrEqualTo: 'less or equal to',
    startsWith: 'starts with',
    endsWith: 'ends with',
    changesValue: 'changes value',
  },
};
const ConditionContainer = styled.div({
  ...lineClamp('-webkit-inline-box', 1, true),
});
